'use client';

import classNames from 'classnames';
import Image from 'next/image';
import { useEffect, useState } from 'react';

import { useAspectRatio } from '@/helpers/hooks';

import { ParallaxOverlayImages } from './ParallaxOverlayImages';

interface HeroSectionProps {
  backgroundColor?: string;
  backgroundImage?: string;
  backgroundPosition?:
    | 'object-bottom'
    | 'object-center'
    | 'object-left-bottom'
    | 'object-left-top'
    | 'object-left'
    | 'object-right-bottom'
    | 'object-right-top'
    | 'object-right'
    | 'object-top';
  heading?: string;
  headingColor?: string;
  mobileBackgroundImage?: string;
  mobileBackgroundPosition?:
    | 'object-bottom'
    | 'object-center'
    | 'object-left-bottom'
    | 'object-left-top'
    | 'object-left'
    | 'object-right-bottom'
    | 'object-right-top'
    | 'object-right'
    | 'object-top';
  mobileOverlayImages?: Array<{
    src: string;
    alt: string;
    className?: string;
    width?: number;
    height?: number;
  }>;
  overlayImages?: Array<{
    src: string;
    alt: string;
    className?: string;
    width?: number;
    height?: number;
  }>;
}

export default function HeroSection({
  backgroundImage = '/placeholder.svg?height=1080&width=1920',
  mobileBackgroundImage = '/placeholder.svg?height=1080&width=1920',
  headingColor = 'text-white',
  heading = 'Your Amazing Headline Here',
  backgroundColor = 'bg-navy',
  backgroundPosition = 'object-center',
  mobileBackgroundPosition = 'object-center',
  overlayImages,
  mobileOverlayImages,
}: HeroSectionProps) {
  const { isLandscape } = useAspectRatio();
  const [mounted, setMounted] = useState(false);
  const [imageLoaded, setImageLoaded] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  const handleImageLoad = () => {
    setImageLoaded(true);
  };

  return (
    <section className="fixed inset-0 h-screen w-full overflow-hidden">
      <div className={classNames('absolute inset-0', backgroundColor)} />

      {mounted && (
        <>
          {/* Background Images */}
          {isLandscape ? (
            <Image
              src={backgroundImage}
              alt="Hero background"
              fill
              className={`object-cover ${backgroundPosition} transition-opacity duration-500 ${
                imageLoaded ? 'opacity-100' : 'opacity-0'
              }`}
              priority
              sizes="100vw"
              onLoad={handleImageLoad}
            />
          ) : (
            <Image
              src={mobileBackgroundImage || backgroundImage}
              alt="Hero background"
              fill
              className={`object-cover ${mobileBackgroundPosition} transition-opacity duration-500 ${
                imageLoaded ? 'opacity-100' : 'opacity-0'
              }`}
              priority
              sizes="100vw"
              onLoad={handleImageLoad}
            />
          )}

          {/* Overlay Images */}
          {isLandscape && overlayImages && <ParallaxOverlayImages images={overlayImages} isLoaded={imageLoaded} />}

          {!isLandscape &&
            mobileOverlayImages?.map((image, index) => {
              return (
                <div key={`mobile-${index}`} className={classNames('absolute', image.className)}>
                  <Image
                    src={image.src}
                    alt={image.alt}
                    width={image.width}
                    height={image.height}
                    fill
                    className="object-contain"
                  />
                </div>
              );
            })}
        </>
      )}

      <div className="absolute inset-0" />
      <div className="absolute bottom-[45px] left-[45px] mobile-only:right-[45px]">
        {heading && (
          <h1
            className={`kerning-[-4px] font-breit font-fett ${headingColor} leading-none`}
            style={{
              // Cap the heading at the design size, but shrink it on small screens so the word
              // fits between equal 45px margins (100vw - 90px) instead of bleeding off the right.
              fontSize:
                'min(clamp(64px, calc(64px + (200 - 64) * (100vw - 375px) / (1920 - 375)), 200px), calc((100vw - 90px) / 5.7))',
            }}
          >
            {heading}
          </h1>
        )}
      </div>
    </section>
  );
}
